/**
 * Google Analytics 4 (gtag.js) の薄いラッパー (JPTP_CANSEI-1400)。
 *
 * 測定 ID はアプリごとに異なる (guest / concierge で別データストリーム) ため、
 * 各アプリが自分の環境変数から読んで initAnalytics へ渡す。未指定 (ローカル・dev) の場合は
 * すべての関数が no-op になり、GA へは一切送信しない。
 *
 * gtag.js の npm パッケージは提供されていないため、script タグを動的に注入する。
 * index.html への直書きは Bun のバンドラが HTML 内の process.env を置換しない
 * (BUN_PUBLIC_* のリテラル参照のみが対象) ため環境別の測定 ID を切り替えられず、採用できない。
 */

export type ParamValue = string | number | boolean | undefined;
export type Params = Record<string, ParamValue>;

/**
 * 全イベントに付与されるページ情報。GA4 は page_location / page_referrer / page_title を
 * 自動付与するため、素の URL を送らないよう set で既定値ごと上書きする (setPageContext)。
 */
export type PageContext = {
  page_location: string;
  page_referrer?: string;
  page_title?: string;
};

type GtagCommand =
  | ["js", Date]
  | ["config", string, Params?]
  | ["event", string, Params?]
  | ["set", Params];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagCommand) => void;
  }
}

// 有効な測定 ID。initAnalytics 済みかどうかの判定も兼ねる (null = 未初期化 or 無効)。
let measurementId: string | null = null;
// 直前に送信した page_location。SPA 内遷移の page_referrer に使う。
let lastLocation: string | undefined;

/**
 * 重要: 公式スニペットと同じく arguments オブジェクトを push する。rest 引数 (...args) で
 * 配列を push すると gtag.js は GTM 由来の push として解釈し、window.gtag は存在するのに
 * ビーコンが一切飛ばない (例外も出ないため気付けない)。
 */
function gtagQueue() {
  // biome-ignore lint/complexity/noArguments: 上記のとおり arguments オブジェクトが必須
  window.dataLayer?.push(arguments);
}
const gtag = gtagQueue as (...args: GtagCommand) => void;

/**
 * gtag.js を読み込んで初期化する。measurementId が空なら何もしない (全送信 no-op)。
 * 二重初期化は無視する。
 *
 * send_page_view: false にして page_view は手動送信に一本化する。GA4 の拡張計測
 * (履歴イベントに基づくページの変更) もデータストリーム側で OFF にしてあり、
 * 自動送信が素の URL (guest はアクセストークンを含む) を送るのを防いでいる。
 */
export function initAnalytics(id: string | undefined, context: PageContext): void {
  if (!id || measurementId !== null) return;
  measurementId = id;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = gtag;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);

  gtag("js", new Date());
  // config より先に set する。config が発火させる初回イベントにも素の URL を載せない。
  setPageContext(context);
  gtag("config", id, { send_page_view: false });
}

/** 全イベントに付与されるページ情報の既定値を上書きする。 */
export function setPageContext(context: PageContext): void {
  if (measurementId === null) return;
  gtag("set", { ...context });
}

/** User-ID を設定する。個人を特定できる値 (メールアドレス・氏名) は渡さないこと。 */
export function setUserId(userId: string): void {
  if (measurementId === null) return;
  gtag("set", { user_id: userId });
}

/** 任意のイベントを送信する。 */
export function trackEvent(name: string, params?: Params): void {
  if (measurementId === null) return;
  gtag("event", name, params);
}

/**
 * page_view を送信する。location は伏字処理済みの完全な URL を渡すこと
 * (guest のアクセストークン・concierge の案件 UUID は呼び出し側で除去する)。
 * page_referrer は直前に送った page_location (初回のみ外部リファラー) を使う。
 */
export function trackPageView(location: string, params?: Params): void {
  if (measurementId === null) return;
  const context: PageContext = {
    page_location: location,
    page_referrer: lastLocation ?? externalReferrer(),
    page_title: document.title,
  };
  // 後続の click イベント等にも同じページ情報が載るよう、送信前に既定値を差し替える。
  setPageContext(context);
  gtag("event", "page_view", { ...context, ...params });
  lastLocation = location;
}

/**
 * 自サイト内の document.referrer は伏字処理前の素の URL (アクセストークン込み) なので使わない。
 * 外部サイトから来た場合のみリファラーとして採用する。
 */
function externalReferrer(): string | undefined {
  const referrer = document.referrer;
  return !referrer || referrer.startsWith(`${window.location.origin}/`) ? undefined : referrer;
}
